import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import SubcategoryDetail from '../views/SubcategoryDetail.vue'
import CategoryListing from '../views/CategoryListing.vue'
import { useAuthStore } from "../stores/auth.js";
import { useDataStore } from "@/stores/dataStore.js";
import Profile from '../views/panel/Profile.vue';
import Orders from '../views/panel/Orders.vue';
import TransactionHistory from '../views/panel/TransactionHistory.vue';
import LoginHistory from '../views/panel/LoginHistory.vue';
import ChargeWallet from '../views/panel/ChargeWallet.vue';
import NotificationsPage from '../views/panel/NotificationsPage.vue';
import UserPanelLayout from "@/layouts/UserPanelLayout.vue";
import PaymentHistory from "@/views/PaymentHistory.vue";
import NotFound from '../views/error/404.vue';
import Forbidden from '../views/error/403.vue';
import Unauthorized from '../views/error/401.vue';
import InternalServerError from '../views/error/500.vue';
import ServiceUnavailable from '../views/error/503.vue';

const routes = [
    // Static routes FIRST (highest priority)
    {
        path: '/',
        name: 'home',
        component: Home
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/LoginView.vue'),
        meta: {
            requiresAuth: false,
            standalone: true
        }
    },
    {
        path: '/about',
        name: 'about',
        component: () => import('../views/useful/AboutView.vue')
    },
    {
        path: '/contact',
        name: 'contact',
        component: () => import('../views/ContactView.vue')
    },
    {
        path: '/terms',
        name: 'terms',
        component: () => import('../views/useful/TermsView.vue')
    },
    {
        path: '/faq',
        name: 'faq',
        meta: {
            title: 'FAQ - Eskrag'
        },
        component: () => import('../views/useful/FaqView.vue')
    },
    {
        path: '/privacy',
        name: 'privacy',
        component: () => import('../views/useful/PrivacyView.vue')
    },
    {
        path: '/refund',
        name: 'refund',
        component: () => import('../views/useful/RefundView.vue')
    },
    {
        path: '/cookie-policy',
        name: 'cookie-policy',
        component: () => import('../views/useful/CookiePolicyView.vue'),
        meta: { title: 'Cookie Policy - Eskrag' }
    },
    {
        path: '/checkout/:step(summary|signup|payment|confirmation)?',
        name: 'checkout',
        component: () => import('../views/Checkout.vue'),
        meta: { requiresAuth: false, requiresCartAllowed: true, layout: 'MainLayout', title: 'Checkout - Eskrag' }
    },
    {
        path: '/order-success',
        name: 'order-success',
        component: () => import('../views/OrderSuccess.vue'),
        meta: {
            requiresAuth: false,
            layout: 'MainLayout',
            title: 'Order Success - Eskrag'
        }
    },
    {
        path: '/physical-order/success',
        name: 'physical-order-success',
        component: () => import('../views/PhysicalOrderSuccess.vue'),
        meta: {
            requiresAuth: false,
            layout: 'MainLayout',
            title: 'Physical Order Success - Eskrag'
        }
    },
    {
        path: '/physical-order/success/:orderNumber',
        name: 'physical-order-success-legacy',
        component: () => import('../views/PhysicalOrderSuccess.vue'),
        meta: {
            requiresAuth: false,
            layout: 'MainLayout',
            title: 'Physical Order Success - Eskrag'
        }
    },
    {
        path: '/all-products',
        name: 'all-products',
        component: () => import('../views/AllProductsView.vue'),
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/search',
        name: 'search',
        component: () => import('../views/SearchView.vue'),
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../views/RegisterView.vue'),
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('../views/ForgotPasswordView.vue'),
        meta: {
            requiresAuth: false,
            standalone: true
        }
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('../views/ResetPasswordView.vue'),
        meta: {
            requiresAuth: false,
            standalone: true
        }
    },

    // Physical Products & Buyback Routes
    {
        path: '/physical-catalog',
        name: 'physical-catalog',
        component: () => import('../views/PhysicalCatalog.vue'),
        meta: {
            requiresAuth: false,
            title: 'Hardware Store & Catalog - Eskrag'
        }
    },
    {
        path: '/physical-product/:slug',
        name: 'physical-product-detail',
        component: () => import('../views/PhysicalProductDetail.vue'),
        meta: {
            requiresAuth: false,
            title: 'Physical Product - Eskrag'
        }
    },
    {
        path: '/physical-order/checkout/:slug',
        alias: '/physical-checkout/:slug',
        name: 'physical-order-checkout',
        component: () => import('../views/PhysicalCheckout.vue'),
        meta: {
            requiresAuth: false,
            title: 'Hardware Checkout - Eskrag'
        }
    },
    {
        path: '/buyback/sell',
        name: 'buyback-sell',
        component: () => import('../views/BuybackForm.vue'),
        meta: {
            requiresAuth: false,
            title: 'Sell Your Tech - Eskrag'
        }
    },
    {
        path: '/buyback/chat/:id',
        name: 'buyback-chat',
        component: () => import('../views/BuybackChat.vue'),
        meta: {
            requiresAuth: true,
            title: 'Buyback Chat - Eskrag'
        }
    },

    // Panel routes
    {
        path: '/panel',
        component: UserPanelLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                redirect: '/panel/profile'
            },
            {
                path: 'profile',
                name: 'Profile',
                component: Profile,
                meta: {
                    requiresAuth: true,
                    title: 'Profile Settings'
                }
            },
            {
                path: 'orders',
                name: 'Orders',
                component: Orders,
                meta: {
                    requiresAuth: true,
                    title: 'My Orders'
                }
            },
            {
                path: 'physical-orders',
                name: 'PhysicalOrders',
                component: () => import('../views/panel/PhysicalOrders.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'My Physical Orders'
                }
            },
            {
                path: 'my-buybacks',
                name: 'MyBuybacks',
                component: () => import('../views/panel/MyBuybacks.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'My Device Buybacks'
                }
            },
            {
                path: 'transactions',
                name: 'TransactionHistory',
                component: TransactionHistory,
                meta: {
                    requiresAuth: true,
                    title: 'Transaction History'
                }
            },
            {
                path: 'login-history',
                name: 'LoginHistory',
                component: LoginHistory,
                meta: {
                    requiresAuth: true,
                    title: 'Login History'
                }
            },
            {
                path: 'notifications',
                name: 'NotificationsPage',
                component: NotificationsPage,
                meta: {
                    requiresAuth: true,
                    title: 'Notifications'
                }
            },
            {
                path: 'payment-history',
                name: 'PaymentHistory',
                component: PaymentHistory,
                meta: {
                    requiresAuth: true,
                    title: 'Payment History',
                    requiresActiveUser: true,
                }
            },
            {
                path: 'charge-wallet',
                name: 'ChargeWallet',
                component: ChargeWallet,
                meta: {
                    requiresAuth: true,
                    title: 'Charge Wallet',
                    requiresActiveUser: true,
                    requiresHasWallet: true,
                }
            }
        ],
    },

    // Error Pages
    {
        path: '/403',
        name: 'forbidden',
        component: Forbidden,
        meta: {
            standalone: true
        }
    },
    {
        path: '/404',
        name: 'not-found',
        component: NotFound,
        meta: {
            standalone: true
        }
    },
    {
        path: '/401',
        name: 'unauthorized',
        component: Unauthorized,
        meta: {
            standalone: true
        }
    },
    {
        path: '/500',
        name: 'internal-server-error',
        component: InternalServerError,
        meta: {
            standalone: true
        }
    },
    {
        path: '/503',
        name: 'service-unavailable',
        component: ServiceUnavailable,
        meta: {
            standalone: true
        }
    },

    // OLD routes - Redirect to new slug format
    {
        path: '/category/:id',
        redirect: to => {
            const dataStore = useDataStore();
            const slugPath = dataStore.getCategorySlugPath(parseInt(to.params.id));
            return slugPath || '/404';
        }
    },
    {
        path: '/subcategory/:id',
        redirect: to => {
            const dataStore = useDataStore();
            const slugPath = dataStore.getSubcategorySlugPath(parseInt(to.params.id));
            return slugPath || '/404';
        }
    },

    // Recharge (Flexi/Idoom)
    {
        path: '/recharge',
        name: 'recharge',
        component: () => import('../views/flexi.vue'),
        meta: {
            requiresAuth: true,
            requiresRechargeAccess: true,
            title: 'Recharge - Eskrag'
        }
    },

    // Dynamic routes - MUST be after static routes
    // Subcategory route (with 2 segments)
    {
        path: '/:categorySlug/:subcategorySlug',
        name: 'subcategory-detail',
        component: SubcategoryDetail,
        props: true
    },

    // Category route (with 1 segment)
    {
        path: '/:categorySlug',
        name: 'category-listing',
        component: CategoryListing,
        props: true
    },

    // Catch-all route - MUST be absolute last
    {
        path: '/:pathMatch(.*)*',
        name: 'catch-all',
        component: NotFound,
        meta: {
            standalone: true
        }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
        return { top: 0 }
    },
})

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    const dataStore = useDataStore()

    // Routes that don't need data to be loaded first
    const standaloneRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/403', '/404', '/401', '/500', '/503']
    const isStandaloneRoute = standaloneRoutes.includes(to.path) || to.meta.standalone

    // For non-standalone routes, fetch data non-blockingly if already initialized
    if (!isStandaloneRoute) {
        if (!dataStore.isInitialized) {
            try {
                await Promise.race([
                    dataStore.fetchData(),
                    new Promise(resolve => setTimeout(resolve, 1000))
                ]);
            } catch (error) {
                console.error('❌ Failed to load initial data:', error);
            }
        } else {
            // Trigger background refresh without delaying route navigation
            dataStore.fetchData().catch(() => {});
        }
    }

    // Check auth requirements first
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    if (requiresAuth && !authStore.isLoggedIn) {
        return next({
            path: '/login',
            query: { redirect: to.fullPath }
        })
    }

    const requiresActiveUser = to.matched.some(record => record.meta.requiresActiveUser)
    if (requiresActiveUser && authStore.isLoggedIn) {
        // Check if user is active
        if (!authStore.user || authStore.user.is_active !== 1) {
            // Redirect to profile with error message
            return next({
                path: '/403',
                query: {
                    message: 'Your account needs to be activated to access this feature'
                }
            })
        }
    }

    const requiresRechargeAccess = to.matched.some(record => record.meta.requiresRechargeAccess)
    if (requiresRechargeAccess && authStore.isLoggedIn) {
        const country = String(dataStore.country || authStore.user?.country || '');
        const isActive = authStore.user?.is_active === '1' || authStore.user?.is_active === 1;
        const isDZ = country.toLowerCase() === 'dz' || country === '2';
        if (!isDZ || !isActive) {
            return next({
                path: '/403',
                query: { message: 'The recharge service is only available to verified Algerian accounts.' }
            })
        }
    }

    const requiresHasWallet = to.matched.some(record => record.meta.requiresHasWallet)
    if (requiresHasWallet && authStore.isLoggedIn) {
        // Check if user has wallet access
        if (!authStore.user || dataStore.country === 'EG') {
            // Redirect to profile with error message
            return next({
                path: '/403',
                query: {
                    message: 'Charge wallet is not available for your account, if you think this is an error, please Contact us.'
                }
            })
        }
    }

    const requiresCartAllowed = to.matched.some(record => record.meta.requiresCartAllowed)
    if (requiresCartAllowed && dataStore.shouldHideCart) {
        return next({
            path: '/403',
            query: {
                message: 'Checkout is not available for your account, if you think this is an error, please Contact us.'
            }
        })
    }

    if (to.name === 'checkout' && to.params.step === 'signup' && authStore.isLoggedIn) {
        return next({ name: 'checkout', params: { step: 'payment' } })
    }

    if ((to.path === '/login' || to.path === '/register') && !to.query.redirect && from.path && from.path.startsWith('/checkout')) {
        return next({
            path: to.path,
            query: { ...to.query, redirect: '/checkout' }
        })
    }

    next()
})

router.afterEach((to, from) => {
    // Reset page title on every navigation (fixes sticky 404 title)
    const title = to.meta?.title
    if (title) {
        document.title = title
    } else if (to.name !== 'not-found' && to.name !== '404') {
        document.title = 'Eskrag'
    }

    // SPA PageView tracking for Pixels
    if (typeof window !== 'undefined') {
        if (window.fbq) {
            window.fbq('track', 'PageView');
        }
        if (window.ttq && typeof window.ttq.page === 'function') {
            window.ttq.page();
        }
        if (window.gtag) {
            window.gtag('event', 'page_view', {
                page_path: to.fullPath,
                page_title: document.title
            });
        }
    }

    // Check if user is navigating to login/register from checkout
    if ((to.path === '/login' || to.path === '/register') && from.path && from.path.startsWith('/checkout')) {
        localStorage.setItem('redirectAfterLogin', '/checkout')
    }
})

export default router
